import React, { useState } from 'react';
import { GLOBAL_AI_INSIGHTS, APPS_DATA } from '../data/mockData';
import { AIInsightItem } from '../types';

interface AIInsightsViewProps {
  onSelectApp: (appId: string) => void;
}

export const AIInsightsView: React.FC<AIInsightsViewProps> = ({ onSelectApp }) => {
  const [filterType, setFilterType] = useState<string>('all');

  // Collect all insights across all apps plus global
  const allInsights: AIInsightItem[] = [...GLOBAL_AI_INSIGHTS];
  Object.values(APPS_DATA).forEach((app) => {
    app.aiInsights.forEach((i) => {
      if (!allInsights.some((existing) => existing.id === i.id)) {
        allInsights.push(i);
      }
    });
  });

  const filtered = allInsights.filter((item) => {
    if (filterType === 'all') return true;
    return item.type.toLowerCase() === filterType.toLowerCase();
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="pb-6 border-b border-slate-200/80">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold mb-2">
          <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
          Neural Market Synthesis Engine
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
          AI Market Intelligence Stream
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Continuous AI analysis decoding why apps are moving, identifying hidden category inflection points, and flagging sudden install velocity shifts.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none text-xs font-semibold">
        {['all', 'Breakout', 'Anomaly', 'Milestone', 'Competitor'].map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-4 py-2 rounded-2xl transition-all border capitalize ${
              filterType === type
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20 font-bold'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {type === 'all' ? `All Signals (${allInsights.length})` : `${type} Signals`}
          </button>
        ))}
      </div>

      {/* Insights Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-3xl p-6 border border-slate-200/80 hover:border-indigo-300 hover:shadow-lg transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {item.type}
                  </span>
                  {item.appName && item.appId && (
                    <button
                      onClick={() => onSelectApp(item.appId!)}
                      className="text-xs font-bold text-blue-600 hover:underline"
                    >
                      {item.appName} ›
                    </button>
                  )}
                </div>
                <span className="text-xs text-slate-400 font-medium">{item.timestamp}</span>
              </div>

              <h3 className="text-base font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{item.text}</p>
            </div>

            {item.appId && (
              <div className="pt-4 border-t border-slate-100 mt-4 flex items-center justify-between text-xs">
                <span className="text-slate-400 font-medium">Signal verified by AppStatsView Models</span>
                <button
                  onClick={() => onSelectApp(item.appId!)}
                  className="font-bold text-indigo-600 hover:text-indigo-800"
                >
                  Analyze App Profile ›
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
